import React, { useState, useEffect, useMemo, lazy, Suspense } from 'react'
import { Dialog, Disclosure, Popover } from '@headlessui/react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import {
  FaFacebook,
  FaInstagram,
  FaLinkedin,
  FaTwitter,
  FaYoutube
} from 'react-icons/fa'
import CVForm from './CVForm'

// Lazy load CVForm bileşeni
const CVFormComponent = lazy(() => import('./CVForm'))

// Navigasyon menüsü - memoize edildi
const navigation = [
  { name: 'Who We Are', href: '/who-we-are' },
  {
    name: 'For Partners',
    href: '/for-partners',
    submenu: [
      { name: 'Recruitment', href: '/for-partners/recruitment' },
      { name: 'Headhunting', href: '/for-partners/headhunting' },
      { name: 'Career Consulting', href: '/for-partners/career-consulting' },
      { name: 'Personality Inventory Reporting', href: '/for-partners/personality-inventory' },
      { name: 'HR Partnership', href: '/for-partners/hr-partnership' }
    ]
  },
  {
    name: 'For Talents',
    href: '/for-talents',
    submenu: [
      { name: 'Find Your Next Opportunity', href: '/for-talents/opportunities' }
    ]
  },
  {
    name: 'Resources',
    href: '/resources',
    submenu: [
      { name: 'Talent Talks', href: '/resources/talent-talks' },
      { name: 'Blog', href: '/resources/blog' }
    ]
  },
  { name: 'Contact Us', href: '/contact' }
]

// Sosyal medya linkleri - memoize edildi
const socialLinks = [
  { icon: FaLinkedin, href: 'https://www.linkedin.com/company/81917889/' },
  { icon: FaInstagram, href: 'https://www.instagram.com/talentfinderconsulting/' },
  { icon: FaFacebook, href: 'https://www.facebook.com/talentfinderconsulting' },
  { icon: FaTwitter, href: 'https://twitter.com/talentfinderco' },
  { icon: FaYoutube, href: 'https://www.youtube.com/channel/UCQ5-NZ-JZ3-JWvw0ZV0uEEA' }
]

// Mobil menü bileşeni - ayrı bir bileşen olarak tanımlandı
const MobileMenu = ({ items, activeSubmenu, toggleSubmenu, handleBack, isActive }) => {
  return (
    <div className={`fixed inset-0 z-50 transform ${isActive ? 'translate-x-0' : 'translate-x-full'} transition-transform duration-300 ease-in-out`}>
      <div className="relative h-full w-full bg-white">
        <div className="flex items-center justify-between p-4 border-b">
          <button onClick={handleBack} className="text-gray-500">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-lg font-medium">Menu</span>
          <div className="w-6" />
        </div>
        
        {/* Menüler */}
        <div className="p-4">
          {(activeSubmenu ? activeSubmenu.submenu : items).map((item, index) => (
            <div key={item.name}>
              {index > 0 && <hr className="my-2 border-gray-200" />}
              {item.submenu ? (
                <button
                  onClick={() => toggleSubmenu(item)}
                  className="w-full text-left py-2 flex items-center justify-between"
                >
                  {item.name}
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ) : (
                <Link
                  to={item.href}
                  className="block py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              )}
            </div>
          ))}
        </div>
        
        {/* Sosyal Medya İkonları */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
          <div className="flex justify-center gap-4">
            {socialLinks.map((social, index) => (
              <a
                key={index}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-[#592C82]"
              >
                <social.icon className="h-6 w-6" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default function Navbar() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [activeSubmenu, setActiveSubmenu] = useState(null)
  const [showCVForm, setShowCVForm] = useState(false)
  const [formType, setFormType] = useState('role')

  // Submenu toggle fonksiyonu
  const toggleSubmenu = (item) => {
    setActiveSubmenu(item)
  }

  // Geri dönme fonksiyonu
  const handleBack = () => {
    setActiveSubmenu(null)
  }

  // Aktif link kontrolü - useMemo ile optimize edildi
  const isActive = useMemo(() => {
    return (href) => location.pathname === href;
  }, [location.pathname]);

  return (
    <header className="bg-transparent pt-6">
      <nav className="mx-auto max-w-7xl px-6 lg:px-8" aria-label="Top">
        <div className="flex h-18 items-center">
          {/* Logo */}
          <div className="flex items-center flex-1">
            <Link to="/" className="flex items-center">
              <img 
                src="/assets/images/logo/default-logo.svg"
                alt="TalentFinder Logo"
                className="w-48 h-auto object-contain"
              />
            </Link>
          </div>

          {/* Menüler */}
          <div className="hidden lg:flex lg:items-center lg:justify-center lg:flex-1">
            <div className="flex gap-x-8 whitespace-nowrap">
              {navigation.map((item) => (
                <div key={item.name} className="relative group">
                  {item.submenu ? (
                    <Popover className="relative">
                      <Popover.Button className="flex items-center gap-x-1 text-lg font-semibold leading-6 text-gray-900 outline-none whitespace-nowrap">
                        {item.name}
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                        </svg>
                      </Popover.Button>

                      <Popover.Panel className="absolute left-1/2 z-10 mt-3 w-screen max-w-md -translate-x-1/2 transform px-4 sm:px-0 lg:max-w-3xl">
                        <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                          <div className="relative grid gap-8 bg-white p-7">
                            {item.submenu.map((subItem) => (
                              <Link
                                key={subItem.name}
                                to={subItem.href}
                                className="flex items-center p-2 -m-3 rounded-lg hover:bg-gray-50 whitespace-nowrap"
                              >
                                <div className="ml-4">
                                  <p className="text-base font-medium text-gray-900">
                                    {subItem.name}
                                  </p>
                                </div>
                              </Link>
                            ))}
                          </div>
                        </div>
                      </Popover.Panel>
                    </Popover>
                  ) : (
                    <NavLink
                      to={item.href}
                      className={({ isActive }) =>
                        isActive
                          ? 'text-lg font-semibold leading-6 text-[#592C82] whitespace-nowrap'
                          : 'text-lg font-semibold leading-6 text-gray-900 hover:text-[#592C82] whitespace-nowrap'
                      }
                    >
                      {item.name}
                    </NavLink>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Sosyal Medya */}
          <div className="hidden lg:flex lg:items-center lg:flex-1 lg:justify-end">
            <div className="flex items-center gap-x-4">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-[#592C82]"
                >
                  <social.icon className="h-6 w-6" />
                </a>
              ))}
            </div>
          </div>

          {/* Mobil Menü Butonu */}
          <div className="flex lg:hidden">
            <button
              type="button"
              className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
              onClick={() => setMobileMenuOpen(true)}
            >
              <span className="sr-only">Open main menu</span>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
              </svg>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobil menü */}
      <MobileMenu
        items={navigation}
        activeSubmenu={activeSubmenu}
        toggleSubmenu={toggleSubmenu}
        handleBack={handleBack}
        isActive={mobileMenuOpen}
      />
      
      {/* CV Form Modal */}
      {showCVForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowCVForm(false)} />
            
            <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">&#8203;</span>
            
            <div className="relative inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:align-middle">
              <div className="absolute right-0 top-0 pr-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCVForm(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <Suspense fallback={
                <div className="flex items-center justify-center h-96">
                  <div className="w-12 h-12 border-4 border-gray-300 rounded-full border-t-purple-600 animate-spin"></div>
                </div>
              }>
                <CVForm onSuccess={() => setShowCVForm(false)} initialFormType={formType} />
              </Suspense>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}